import { type TemplateVariableFieldType } from "commonComponents/btWrappers/editor/core/extensions/custom/templateVariable/TemplateVariableExtension";
import { CurrencyDisplay } from "commonComponents/financial/CurrencyDisplay/CurrencyDisplay";
import { DateDisplayDate } from "commonComponents/utilities/DateDisplay/DateDisplayDate";

interface ITemplateVariableValueDisplayProps {
    value: string | number | Date;
    type: TemplateVariableFieldType;
}

/**
 * Renders a resolved chip value per its declared field type so currency/date use the same
 * locale-aware components as the rest of the app:
 *  - a `string` always renders verbatim, regardless of `type`. This is how a caller passes a
 *    pre-resolved fallback message (e.g. "Will display amount when payment is sent", or a
 *    permission-gated string from the server) in place of a real number/date;
 *  - a raw `number` on a `"currency"` field renders through `<CurrencyDisplay>`;
 *  - a raw `Date` on a `"date"` field renders through `<DateDisplayDate>`;
 *  - anything else falls back to the value's string form.
 */
export function TemplateVariableValueDisplay({ value, type }: ITemplateVariableValueDisplayProps) {
    if (typeof value === "string") {
        return <>{value}</>;
    }

    if (type === "currency" && typeof value === "number") {
        return <CurrencyDisplay value={value} />;
    }

    if (type === "date" && value instanceof Date) {
        return (
            <DateDisplayDate
                value={value}
                data-testid="templateVariableDate"
                showDate
                showTime={false}
            />
        );
    }

    return <>{String(value)}</>;
}
